import React, { useState, useEffect } from "react";
import ApiManager from "../../ApiManager/ApiManager";
import EditEntity from "../../components/EditEntity/EditEntity";
import { useComponentLoading } from "../../utils/useComponentLoading";
import { LoadingSpinner } from "../../components/LoadingSpinner/LoadingSpinner";

const EditFuelTransaction = ({ rowId, onSuccess, toggleFunction}) => {
  const [currentRowId, setCurrentRowId] = useState(rowId);
  const [isModalOpen, setModalOpen] = useState(false);
  const [carOptions, setCarOptions] = useState([]);
  const [driverOptions, setDriverOptions] = useState([]);
  const [fuelFields, setFuelFields] = useState([]);
  const [carMap, setCarMap] = useState({});
  const [driverMap, setDriverMap] = useState({});
  const [fileResponse, setFileResponse] = useState(null);
  const [fuelTransactionData, setFuelTransactionData] = useState({});
  const [showLoading, setShowLoading] = useState(false);
  
  // Use the loading hook
  const { withLoading, isLoading, getMessage, getProgress } = useComponentLoading();

  console.log("id in edit fuel transaction", rowId);

  useEffect(() => {
    if (rowId) setCurrentRowId(rowId);
  }, [rowId]);

  useEffect(() => {
    loadData();
    console.log("id in edit fuel transaction in useeffect", rowId);
  }, []);

  const loadData = async () => {
    setShowLoading(true);
    
    await withLoading('loadEditFuelData', async (onProgress) => {
      onProgress(10);
      await fetchDriverData();
      onProgress(40);
      await fetchCarData();
      onProgress(70);
      await fetchFuelTransactionData();
      onProgress(100);
      
      // Add a small delay to ensure the loading indicator is visible
      setTimeout(() => {
        setShowLoading(false);
      }, 500);
    }, 'Loading fuel transaction data...', 30000);
  };

  const fetchDriverData = async () => {
    try {
      const params = new URLSearchParams({
        page_size: 1000,
        page: 1,
      });
      const response = await ApiManager.getAllDrivers(params);
      if (!response.ok) throw new Error("Network response was not ok");
      const json = await response.json();
      const driverNames = json.map(
        (driver) => driver.firstName + " " + driver.lastName
      );
      const driverIdMap = json.reduce((map, driver) => {
        map[driver.firstName + " " + driver.lastName] = driver.id;
        return map;
      }, {});
      setDriverOptions(driverNames);
      setDriverMap(driverIdMap);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const fetchFuelTransactionData = async () => {
    try {
      const query = new URLSearchParams({
        id: currentRowId,
      });
      const response = await ApiManager.getFuelTransaction(query);
      if (!response.ok) throw new Error("Network response was not ok");
      const json = await response.json();
      setFuelTransactionData(json);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const fetchCarData = async () => {
    try {
      const params = new URLSearchParams({
        page_size: 1000,
        page: 1,
      });
      const response = await ApiManager.getAllCars(params);
      if (!response.ok) throw new Error("Network response was not ok");
      const json = await response.json();
      const carNumbers = json.map((car) => car.vehicleNo);
      const carIdMap = json.reduce((map, car) => {
        map[car.vehicleNo] = car.id;
        return map;
      }, {});
      setCarOptions(carNumbers);
      setCarMap(carIdMap);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    setFuelFields([
      ...(carOptions.length > 0
        ? [
            {
              label: "Car",
              name: "car_number",
              type: "picklist",
              required: true,
              options: carOptions,
            },
          ]
        : [
            {
              label: "Car",
              name: "car_number",
              type: "picklist",
              required: true,
              options: [],
            },
          ]),
      ...(driverOptions.length > 0
        ? [
            {
              label: "Driver",
              name: "driver_name",
              type: "picklist",
              required: true,
              options: driverOptions,
            },
          ]
        : [
            {
              label: "Driver",
              name: "driver_name",
              type: "picklist",
              required: true,
              options: [],
            },
          ]),
      {
        label: "Fuel Quantity (L)",
        name: "fuel_amount",
        type: "number",
        required: true,
      },
      { label: "Amount", name: "amount", type: "number", required: true },
      {
        label: "Km Reading",
        name: "km_reading",
        type: "number",
        required: true,
      },
      {
        label: "Fuel Type",
        name: "fuel_type",
        type: "picklist",
        required: true,
        options: ["PETROL", "DIESEL", "CNG", "ELECTRIC", "OTHER"],
      },
      {
        label: "Fuel Receipt",
        name: "file_path_fuel",
        type: "file",
        required: false,
      },
      {
        label: "km reading file",
        name: "file_path_km_reading",
        type: "file",
        required: false,
      },
      {
        label: "Serial Number",
        name: "description",
        type: "text",
        required: false,
      },
      {
        label: "Driver Paid",
        name: "is_paid_by_driver",
        type: "checkbox",
        required: false,
      },
    ]);
  }, [carOptions, driverOptions]);

  const handleEditFuelTransaction = async (formData) => {
    console.log("fileResponse", fileResponse);

    const carId = carMap[formData.car_number];
    const driverId = driverMap[formData.driver_name];

    const updatedFormData = {
      ...formData,
      car_id: carId,
      driver_id: driverId,
    };

    if (fileResponse?.file_path_fuel) {
      updatedFormData.file_path_fuel = fileResponse.file_path_fuel;
    }
    if (fileResponse?.file_path_km_reading) {
      updatedFormData.file_path_km_reading = fileResponse.file_path_km_reading;
    }

    const query = new URLSearchParams({
      id: currentRowId,
    });
    const response = await ApiManager.editFuelTransaction(
      query,
      updatedFormData
    );
    return response;
  };

  // Get loading state for the component
  const isDataLoading = isLoading('loadEditFuelData');
  const loadingMessage = getMessage('loadEditFuelData');
  const loadingProgress = getProgress('loadEditFuelData');

  return (
    <>
      {showLoading ? (
        <div className="loading-container">
          <LoadingSpinner 
            size="large" 
            message={loadingMessage || "Loading fuel transaction data..."} 
            progress={loadingProgress}
            showProgress={true}
          />
        </div>
      ) : (
        <EditEntity
          prevData={fuelTransactionData}
          updateFunction={handleEditFuelTransaction}
          columns={fuelFields}
          toggleFunction={toggleFunction}
          setFileResponse={setFileResponse}
          fileResponse={fileResponse}
          onSuccess={onSuccess}
        />
      )}
    </>
  );
};

export default EditFuelTransaction;
